<script setup lang="ts">
import axios from 'axios'
import { Lock, Phone, TriangleAlert } from 'lucide-vue-next'

import type { ChipColor } from '@/components/base/BaseChip.vue'

const props = defineProps<{
  selectedStoreId: number | undefined
}>()

const isLoaded = ref(false)
const params = ref([])
const stores = ref([])
const availableStoresIds = ref([])
const { t } = useI18n()

const { hasPermissionBySlug } = useAccess()
const { formatPhoneNumber } = useNumber()
const { stores: allStores } = useLocalStorage()
const { getStoresByPermission, getStoreSchedulingPlatformUrl } = useStore()
const { openInNewTab } = useDOM()

const hasFilter = computed(() => {
  return !!props.selectedStoreId
})
const storesWithPermission = computed(() => {
  return getStoresByPermission('missed-call.read')
})

const getMissedCallsInfo = async (storeId) => {
  isLoaded.value = false
  try {
    const missedCalls = await axios.get(`missed-calls/stores/${storeId}`)
    params.value = missedCalls.data.data
    if (hasFilter.value) {
      availableStoresIds.value.push(props.selectedStoreId)
      filteredStoreInfo(missedCalls.data.data, storeId)
    } else {
      unfilteredStoreInfo(missedCalls.data.data, storeId)
    }
  } catch {
    const storeHasPermission = storesWithPermission.value.some((store) => store.id === props.selectedStoreId)
    if (storeHasPermission) {
      availableStoresIds.value.push(props.selectedStoreId)
    }
    if (hasFilter.value) {
      filteredStoreInfo('', storeId)
    }
  } finally {
    isLoaded.value = true
  }
}

const filteredStoreInfo = (data, storeId) => {
  const store = allStores.value.find((e) => e.id === storeId)
  store.missedCallsNumber = data.virtualPhoneNumber ?? null
  stores.value.push(store)
}

const unfilteredStoreInfo = (data, storeId) => {
  const store = stores.value.filter((e) => e.id === storeId)[0]
  store.missedCallsNumber = data.virtualPhoneNumber
}
const fetchInfos = async () => {
  if (props.selectedStoreId) {
    stores.value = []
    await getMissedCallsInfo(props.selectedStoreId)
  } else {
    allStores.value.forEach((store) => stores.value.push(store))
    for (const store of storesWithPermission.value) {
      availableStoresIds.value.push(store.id)
      await getMissedCallsInfo(store.id)
    }
  }
}

watch(
  () => props.selectedStoreId,
  () => fetchInfos()
)
onMounted(async () => {
  fetchInfos()
})

const canShowSettingsButton = (storeElement) =>
  availableStoresIds.value.includes(storeElement.id) &&
  storeElement.missedCallsNumber &&
  hasPermissionBySlug('missed-call.update')

const canShowConfigureButton = (storeElement) =>
  availableStoresIds.value.includes(storeElement.id) &&
  !storeElement.missedCallsNumber &&
  hasPermissionBySlug('missed-call.create')

const canShowActivateButton = (storeElement) =>
  !availableStoresIds.value.includes(storeElement.id) && hasPermissionBySlug('missed-call.create')

const getLabelColor = (store): ChipColor => {
  if (availableStoresIds.value.includes(store.id)) {
    return store.missedCallsNumber ? 'green' : 'yellow'
  }
  return 'slate'
}
const redirectToCalendar = (storeId: string | number) => {
  openInNewTab(
    `${getStoreSchedulingPlatformUrl(
      storeId
    )}?utm_source=pongo&utm_medium=dashboard&utm_campaign=upsell&utm_content=appels`
  )
}
</script>

<template>
  <ul v-if="isLoaded" class="grid grid-cols-1 gap-6 lg:grid-cols-2 2xl:grid-cols-3">
    <li
      v-for="storeElement in stores"
      :key="storeElement.id"
      class="col-span-1 flex grow flex-col justify-between rounded-lg border bg-white opacity-100 transition-opacity"
    >
      <div class="flex grow flex-col justify-between">
        <h3 class="px-6 py-3 text-lg font-semibold">
          {{ storeElement.name }}
        </h3>
        <div v-if="availableStoresIds.includes(storeElement.id)" class="border-t p-6 text-muted-foreground">
          <template v-if="storeElement.missedCallsNumber">
            <p class="mb-2 text-sm">{{ t('pages.missedCalls.missedCallsStores.yourNumberIs') }}</p>
            <BaseChip
              :text="formatPhoneNumber(`+${storeElement.missedCallsNumber}`)"
              :color="getLabelColor(storeElement)"
            >
              <template #leading>
                <Phone class="h-3 w-3" />
              </template>
            </BaseChip>
          </template>
          <template v-else>
            <p class="mb-2 text-sm">{{ t('pages.missedCalls.missedCallsStores.toConfigure') }}</p>
            <BaseChip
              :text="t('pages.missedCalls.missedCallsStores.label.toConfigure')"
              :color="getLabelColor(storeElement)"
            >
              <template #leading>
                <TriangleAlert class="h-3 w-3" />
              </template>
            </BaseChip>
          </template>
        </div>
        <div v-else class="border-t p-6 text-muted-foreground">
          <p class="mb-2 text-sm">{{ t('pages.missedCalls.missedCallsStores.notConfigurated') }}</p>
          <BaseChip :text="t('pages.missedCalls.missedCallsStores.label.disabled')" color="slate">
            <template #leading>
              <Lock class="h-3 w-3" />
            </template>
          </BaseChip>
        </div>
        <div
          v-if="
            canShowSettingsButton(storeElement) ||
            canShowConfigureButton(storeElement) ||
            canShowActivateButton(storeElement)
          "
          class="px-6 pb-3"
        >
          <Button
            v-if="canShowSettingsButton(storeElement)"
            variant="outline-primary"
            @click="$router.push({ name: 'MissedCallEdit', params: { storeId: storeElement.id } })"
          >
            {{ t('pages.missedCalls.missedCallsStores.label.settings') }}
          </Button>
          <Button
            v-else-if="canShowConfigureButton(storeElement)"
            variant="outline-primary"
            @click="$router.push({ name: 'MissedCallEdit', params: { storeId: storeElement.id } })"
          >
            {{ t('pages.missedCalls.missedCallsStores.label.configure') }}
          </Button>
          <Button v-else variant="outline-primary" @click="redirectToCalendar(storeElement.id)">
            {{ t('pages.missedCalls.missedCallsStores.label.activate') }}
          </Button>
        </div>
      </div>
    </li>
  </ul>
  <AppSkeleton v-else type="section" />
</template>
